import React, { FC, ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { colors, mixins, spacingCurves } from '../../core/styles/theme/default';
import { pageOffsets } from '../../core/styles/theme/layout';

const StyledBurrito = styled.div(
  ({
    hasBackground,
    spacingCurve,
    topSpace,
    bottomSpace,
    leftSpace,
    rightSpace,
  }: BurritoProps) => {
    const rule: string = hasBackground ? 'padding' : 'margin';
    const cssProps: { [key: string]: string } = {
      position: 'relative',
      'z-index': '0',
    };
    const spacingProps = [
      ...(topSpace ? [`${rule}-top`] : []),
      ...(bottomSpace ? [`${rule}-bottom`] : []),
      ...(leftSpace ? [`${rule}-left`] : []),
      ...(rightSpace ? [`${rule}-right`] : []),
    ];

    const getSpacing = () => {
      if (!spacingCurve) return;

      const propsCurves = spacingProps.reduce(
        (
          acc: { [key: string]: { [key: string]: number } },
          spacingProp: string,
        ) => {
          const { component: componentSpacings } = spacingCurves;
          const propCurve = componentSpacings[spacingCurve](spacingProp);

          return { ...acc, ...propCurve };
        },
        {},
      );

      return mixins.responsiveStyles(propsCurves);
    };

    return [getSpacing(), cssProps];
  },
);

const StyledBurritoOffsetBackground = styled.div(
  ({ backgroundColor, enableOffset }: BurritoBackgroundProps) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${backgroundColor ?? colors.blue100};
    z-index: -1;

    ${enableOffset &&
    mixins.responsiveStyles({
      'margin-left': {
        zero: -1 * pageOffsets.zero,
        small: -1 * pageOffsets.small,
        medium: -1 * pageOffsets.medium,
        large: -1 * pageOffsets.large,
        wide: -1 * pageOffsets.ultra,
        ultra: -1 * pageOffsets.ultra,
      },
      width: {
        zero: `calc(100% + 2 * ${pageOffsets.zero}px)`,
        small: `calc(100% + 2 * ${pageOffsets.small}px)`,
        medium: `calc(100% + 2 * ${pageOffsets.medium}px)`,
        large: `calc(100% + 2 * ${pageOffsets.large}px)`,
        wide: `calc(100% + 2 * ${pageOffsets.ultra}px)`,
        ultra: `calc(100% + 2 * ${pageOffsets.ultra}px)`,
      },
    })}
  `,
);

const Burrito: FC<BurritoProps> = ({
  children,
  hasBackground,
  backgroundColor,
  backgroundOffset = true,
  spacingCurve,
  topSpace = true,
  bottomSpace = true,
  leftSpace,
  rightSpace,
  ...rest
}: BurritoProps) => {
  const mergedProps = {
    hasBackground,
    spacingCurve,
    topSpace,
    bottomSpace,
    leftSpace,
    rightSpace,
  };

  return (
    <StyledBurrito {...mergedProps} {...rest}>
      {hasBackground && (
        <StyledBurritoOffsetBackground
          className={'offsetBackground'}
          backgroundColor={backgroundColor}
          enableOffset={backgroundOffset}
        />
      )}
      {children}
    </StyledBurrito>
  );
};

type BurritoBackgroundProps = {
  backgroundColor?: BurritoProps['backgroundColor'];
  enableOffset?: BurritoProps['backgroundOffset'];
};

export type BurritoProps = {
  /** Show background behind the Content */
  hasBackground?: boolean;
  /** Color of the background behind the Content */
  backgroundColor?: string;
  /** Determines if the background should have horizontal offset */
  backgroundOffset?: boolean;
  /** SpacingCurve name */
  spacingCurve?: string;
  /** Top space */
  topSpace?: boolean;
  /** Bottom space */
  bottomSpace?: boolean;
  /** Left space */
  leftSpace?: boolean;
  /** Right space */
  rightSpace?: boolean;
  /** Children of the Burrito */
  children?: ReactNode;
};

export default Burrito;
